import type { AskCard, PageRef, StatusLine } from '@shared/api';
import type { AssistantPart, ChatMessage } from './useChat';
import type { QueuedChatRequest } from './chatQueue';
import { decodeEntities } from '../lib/entities';

export type TaskRowState = 'running' | 'queued' | 'waiting' | 'done';

/**
 * Done rows only (docs/message-bar-states.md §5): which of the four looks a
 * finished turn gets. `change` and `stopped` render `summaryLine` alone, at
 * the large question style, and close themselves after a few seconds.
 * `answer` and `error` render `text`/`error` at the normal body style and
 * stay open until the rep acts (Ok / Retry).
 */
export type TaskRowKind = 'change' | 'answer' | 'error' | 'stopped';

export interface TaskRow {
  /** Stable React key: the assistant message id, or the queued request's userId. */
  key: string;
  state: TaskRowState;
  /** The request as typed — the source instruction for every row kind. */
  request: string;
  /** Assistant reply text so far (running/waiting/done). */
  text: string;
  ask: AskCard | null;
  /**
   * Done rows only: what the turn actually changed, from the `done` event's
   * `summary` (oldest first). Kept for callers that want the full list;
   * the row itself only ever shows `summaryLine`, the first entry
   * (docs/message-bar-states.md §5: a change row is one line). Empty on
   * every other row state.
   */
  summary: string[];
  /** Done rows only: the turn saved a version past what the sender was looking at (docs/composer-cards.md 5c). */
  undo: boolean;
  /** The proposal version the sender was looking at when this row's request was sent — Undo restores to this version, not one step back. Null for a queued row, which has none to report. */
  versionAtSend: number | null;
  error: string | null;
  stopped: boolean;
  /** Running rows only: the name of the last tool part seen. */
  activeTool: string | null;
  /**
   * Running (and waiting) rows: the latest `status` event for this turn,
   * null before the first one arrives. The running row shows `status.label`,
   * falling back to `toolPhrase(activeTool)` until then. Null on queued and
   * done rows.
   */
  status: StatusLine | null;
  /**
   * The page chip's source (message-bar §2, "no page → no chip" when null):
   * queued rows use the page the rep was viewing when they sent; running,
   * waiting and done rows use the last page any `status` event named this
   * turn, falling back to that same send-time page until one does.
   */
  page: PageRef | null;
  /** Done rows only: which look the row takes. Null on every other state. */
  kind: TaskRowKind | null;
  /**
   * Done rows of kind `change` or `stopped` only: the single line the row
   * renders at the large style — the first line of `summary[0]`, or
   * "Stopped."/"Undone. Back to v{n}." Null for `answer`/`error`, which
   * render `text`/`error` instead, and for every non-done row.
   */
  summaryLine: string | null;
  /** Workflow-owned asks may require an explicit answer and therefore omit Skip. */
  dismissible?: boolean;
  /**
   * Waiting rows only: the turn is still running behind the question (a
   * `keepWorking` ask, docs/decisions.md §6) — the row keeps the running
   * status line (`status`/`activeTool`, with Stop) above the answers so the
   * rep sees "Researching Samuel's Sweet Shop…" AND the budget options at
   * once. Absent once the turn ends.
   */
  working?: boolean;
}

/** The text up to its first newline — a change summary is meant to be one line, but this guards against a stray multi-line one slipping through. */
function firstLine(value: string): string {
  const index = value.indexOf('\n');
  return index === -1 ? value : value.slice(0, index);
}

// Guide Step 5's tool → status-line table.
const TOOL_PHRASES: Array<[string[], string]> = [
  [['get_proposal', 'get_proposal_section', 'get_rate_card'], 'Reading the proposal…'],
  [['resolve_advertiser_website', 'research_advertiser', 'find_logo'], 'Reading their site…'],
  [['search_audience_topics', 'estimate_audience_size', 'size_audience_segment'], 'Sizing the audience…'],
  [
    [
      'recommend_media_mix',
      'price_media_plan',
      'generate_tiers',
      'auto_fix_plan',
      'validate_plan',
      'estimate_reach_frequency',
    ],
    'Pricing the plan…',
  ],
  [['update_proposal'], 'Applying the change…'],
  [['plan_document_composition', 'refresh_document_composition', 'validate_composition_plan'], 'Planning the pages…'],
  [['build_document', 'generate_proposal'], 'Building the pages…'],
  [['update_document'], 'Editing the page…'],
  [['audit_document', 'audit_composition'], 'Checking the deck…'],
  [['search_stock_images', 'set_section_image', 'set_audience_segment_image'], 'Finding images…'],
  [['undo', 'revert_to_version', 'list_versions'], 'Restoring…'],
];

/** The plain-language status line for a running row, from its last tool call. */
export function toolPhrase(name: string | null): string {
  if (name) {
    for (const [names, phrase] of TOOL_PHRASES) {
      if (names.includes(name)) return phrase;
    }
  }
  return 'Working…';
}

function lastToolName(parts: AssistantPart[]): string | null {
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const part = parts[i];
    if (part && part.kind === 'tool') return part.name;
  }
  return null;
}

function findAskPart(parts: AssistantPart[]): AskCard | null {
  const found = parts.find((part): part is { kind: 'ask'; ask: AskCard } => part.kind === 'ask');
  return found ? found.ask : null;
}

function lastToolIndex(parts: AssistantPart[]): number {
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (parts[i]?.kind === 'tool') return i;
  }
  return -1;
}

/**
 * Text for a running/waiting/done row: only the text parts after the last
 * tool call, never the model's pre-tool narration (fix 1). An `ask` part is
 * not a cut point — text between the last tool and an `ask` part is exactly
 * the waiting row's lead line. No tool part at all means the whole reply is
 * fair game. Consecutive text parts join on a newline, not bare
 * concatenation, so nothing runs two sentences together without a space.
 */
function assistantText(parts: AssistantPart[]): string {
  const cut = lastToolIndex(parts);
  return parts
    .slice(cut + 1)
    .filter((part): part is { kind: 'text'; text: string } => part.kind === 'text')
    .map((part) => part.text)
    .join('\n');
}

function findLastAssistantIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') return i;
  }
  return -1;
}

/**
 * Derives the task list from the last turn plus the queue
 * (docs/composer-cards.md 10i): one primary row — running, waiting or done —
 * for the most recent assistant turn, then one queued row per pending
 * request. Pure and React-free so it is directly testable.
 */
export function taskRows(messages: ChatMessage[], queue: QueuedChatRequest[]): TaskRow[] {
  const rows: TaskRow[] = [];
  const assistantIndex = findLastAssistantIndex(messages);

  const assistant = assistantIndex === -1 ? null : messages[assistantIndex] ?? null;
  if (assistant) {
    if (!assistant.dismissed) {
      let userMessage: ChatMessage | null = null;
      for (let i = assistantIndex - 1; i >= 0; i -= 1) {
        const candidate = messages[i];
        if (candidate && candidate.role === 'user') { userMessage = candidate; break; }
      }
      const request = userMessage?.text ?? '';
      // The question still open on this turn, if any. A question the rep
      // already answered, replaced or skipped mid-turn (askSettled, a
      // keepWorking ask — docs/decisions.md §6) is closed; and a turn that
      // errored shows its error rather than a question the rep can no
      // longer usefully answer.
      const askPart = findAskPart(assistant.parts);
      const ask = askPart && !assistant.askSettled && !assistant.error ? askPart : null;
      const versionAtSend = userMessage?.versionAtSend ?? null;
      // Message-bar §2: a status naming a page wins over the page the rep
      // was viewing at send time, and stays won (assistant.statusPage is
      // sticky) even after the turn's done — so this one formula covers
      // running, waiting and done alike.
      const page = assistant.statusPage ?? userMessage?.page ?? null;

      if (assistant.streaming && ask) {
        // Waiting while still working: the question showed mid-turn and the
        // turn carries on behind it. The row is answerable now and keeps
        // the running status line, so the rep sees both.
        rows.push({
          key: assistant.id,
          state: 'waiting',
          request,
          text: assistantText(assistant.parts),
          ask,
          summary: [],
          undo: false,
          versionAtSend,
          error: null,
          stopped: false,
          activeTool: lastToolName(assistant.parts),
          status: assistant.status,
          page,
          kind: null,
          summaryLine: null,
          working: true,
        });
      } else if (assistant.streaming) {
        rows.push({
          key: assistant.id,
          state: 'running',
          request,
          text: assistantText(assistant.parts),
          ask: null,
          summary: [],
          undo: false,
          versionAtSend,
          error: null,
          stopped: false,
          activeTool: lastToolName(assistant.parts),
          status: assistant.status,
          page,
          kind: null,
          summaryLine: null,
        });
      } else if (ask) {
        rows.push({
          key: assistant.id,
          state: 'waiting',
          request,
          text: assistantText(assistant.parts),
          ask,
          summary: [],
          undo: false,
          versionAtSend,
          error: null,
          stopped: false,
          activeTool: null,
          status: assistant.status,
          page,
          kind: null,
          summaryLine: null,
        });
      } else {
        // Once Undo has run, the row reports what happened instead of
        // offering Undo again — a second click cannot revert twice
        // (docs/composer-cards.md 5c). That report is a fact about the undo,
        // not a change the turn itself made, so it displaces any summary
        // from the original turn rather than sitting alongside it.
        const undoneToVersion = assistant.undoneToVersion ?? null;
        const undo = undoneToVersion == null && Boolean(
          versionAtSend != null &&
            assistant.version != null &&
            assistant.version > versionAtSend,
        );
        const text = undoneToVersion != null
          ? `Undone. Back to v${undoneToVersion}.`
          : assistantText(assistant.parts);
        const summary = undoneToVersion != null ? [] : assistant.summary ?? [];
        const stopped = Boolean(assistant.stopped);
        const error = assistant.error;
        // Nothing to tell: no summary, no reply text, and the turn neither
        // stopped nor errored. Render no done row at all rather than an
        // empty one (docs/composer-cards.md 5a).
        if (summary.length > 0 || text || stopped || error) {
          // Priority mirrors what the row actually needs to say: an error
          // wins over everything (the turn didn't finish), a stop reads as
          // just "Stopped." (docs/message-bar-states.md §5 — no partial
          // summary trailing it), then a saved change, then a plain reply.
          let kind: TaskRowKind;
          let summaryLine: string | null = null;
          if (error) {
            kind = 'error';
          } else if (stopped) {
            kind = 'stopped';
            summaryLine = 'Stopped.';
          } else if (undoneToVersion != null) {
            kind = 'change';
            summaryLine = `Undone. Back to v${undoneToVersion}.`;
          } else if (summary.length > 0) {
            kind = 'change';
            summaryLine = firstLine(summary[0]!);
          } else {
            kind = 'answer';
          }

          rows.push({
            key: assistant.id,
            state: 'done',
            request,
            text,
            ask: null,
            summary,
            undo,
            versionAtSend,
            error,
            stopped,
            activeTool: null,
            status: null,
            page,
            kind,
            summaryLine,
          });
        }
      }
    }
  }

  for (const queued of queue) {
    rows.push({
      key: queued.userId,
      state: 'queued',
      request: queued.message,
      text: '',
      ask: null,
      summary: [],
      undo: false,
      versionAtSend: null,
      error: null,
      stopped: false,
      activeTool: null,
      status: null,
      page: queued.page,
      kind: null,
      summaryLine: null,
    });
  }

  return rows.map(decodeRow);
}

/**
 * Model and scraped text can arrive HTML-escaped ("Heating &amp; Air"); every
 * string the bar shows is decoded here once, so no render site has to.
 */
function decodeRow(row: TaskRow): TaskRow {
  return {
    ...row,
    request: decodeEntities(row.request),
    text: decodeEntities(row.text),
    ask: row.ask
      ? {
          ...row.ask,
          question: decodeEntities(row.ask.question),
          answers: row.ask.answers.map((answer) => ({ ...answer, label: decodeEntities(answer.label) })),
        }
      : null,
    summary: row.summary.map(decodeEntities),
    summaryLine: row.summaryLine == null ? null : decodeEntities(row.summaryLine),
    error: row.error == null ? null : decodeEntities(row.error),
    status: row.status ? { ...row.status, label: decodeEntities(row.status.label) } : null,
    page: row.page ? { ...row.page, title: decodeEntities(row.page.title) } : null,
  };
}
